/**
 * SessionDetailsPage
 *
 * Main page for viewing and editing session analysis details.
 * Refactored to use extracted hooks and components for better maintainability.
 *
 * Components used:
 * - SectionGrid: Left sidebar with sections and upload dropzone
 * - ImageViewerPanel: Right panel with image viewer
 * - SessionSettingsDropdown: Settings dropdown panel
 * - FailedDetectionNotification: Toast for failed detection draws
 * - ValidationResultsDialog: Validation results modal
 * - CreateSectionModal: Create section from unassigned image
 * - SectionDetailsDialog: Section details modal
 * - AnalysisProgressModal: Multi-image upload progress
 *
 * Hooks used:
 * - useSessionData: Session and analysis data management
 * - useSessionSettings: Settings management with persistence
 * - useImageUpload: Image upload and analysis processing
 * - useDetectionManagement: Detection CRUD operations
 * - useUserCorrections: User correction tracking
 */

import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import {
  ArrowLeft,
  Upload,
  CheckCircle,
  CheckSquare,
  AlertCircle,
  Settings,
  PenTool,
  FileText,
  Eraser,
  Package,
  X,
} from "lucide-react";
import { Session, HistoryItem, Detection, RoomSection, SectionValidationResult } from "@/types";
import { apiService } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCategoryContext } from "@/contexts/CategoryContext";
import { cn } from "@/utils/cn";
import Tooltip from "@/components/Tooltip";
import SectionGrid from "@/components/section/SectionGrid";
import CreateSectionModal from "@/components/section/CreateSectionModal";
import ValidationResults from "@/components/ValidationResults";
import ValidationResultsDialog from "@/components/ValidationResultsDialog";
import SectionDetailsDialog from "@/components/section/SectionDetailsDialog";
import { useTranslation } from "react-i18next";
import { organizeSectionsWithStatus } from "@/utils/sectionStatus";
import ImageViewerPanel from "@/components/session/ImageViewerPanel";
import FailedDetectionNotification from "@/components/session/FailedDetectionNotification";
import SessionSettingsDropdown from "@/components/session/SessionSettingsDropdown";
import { AnalysisProgressModal } from "@/components/session/AnalysisProgressModal";
import { useIsMobile } from "@/hooks/useMediaQuery";
import MobileSessionView from "@/components/session/mobile/MobileSessionView";

// Types
interface UploadedImage {
  id: string;
  file: File;
  preview: string;
  result?: any;
  isAnalyzing?: boolean;
  error?: string;
  tempAnalysisId?: number;
}

const SessionDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { isAdmin } = useAuth();
  const { categories } = useCategoryContext();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Get all class names from categories
  const allClassNames = useMemo(() => {
    return Object.values(categories).flatMap(cat => cat.classes);
  }, [categories]);

  // ============================================
  // Core State
  // ============================================
  const [session, setSession] = useState<Session | null>(null);
  const [analyses, setAnalyses] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState("");

  // Section state
  const [sections, setSections] = useState<RoomSection[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  const [loadingSections, setLoadingSections] = useState(false);

  // Validation state
  const [validationResult, setValidationResult] = useState<SectionValidationResult | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [validationCache, setValidationCache] = useState<Record<number, SectionValidationResult>>({});
  const loadedValidationSectionIds = useRef<Set<number>>(new Set());

  // Upload state
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [isAnalyzingMultiple, setIsAnalyzingMultiple] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0 });
  const [processedImages, setProcessedImages] = useState<string[]>([]);

  // Settings state
  const [settings, setSettings] = useState({
    confidence_threshold: 0.4,
    k: 5,
    topK: 5,
    conf_threshold: 0.4,
    iou_threshold: 0.5,
    yolo_img_size: 1920,
    cutoff_threshold: 0.85,
    top_distances: 15,
    major_votes: 5,
    show_uncertain_detections: false,
    refiner_enabled: true,
    refiner_threshold: 0.5,
    refiner_trigger_threshold: 1.0,
    disable_manual_detection_discard: false,
  });
  const [defaultSettingsLoaded, setDefaultSettingsLoaded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState(settings);

  // Image selection state
  const [selectedImageId, setSelectedImageIdState] = useState<string | null>(() => {
    if (!sessionId) return null;
    return localStorage.getItem(`selectedImageId-${sessionId}`);
  });

  const setSelectedImageId = (id: string | null) => {
    if (sessionId) {
      if (id) {
        localStorage.setItem(`selectedImageId-${sessionId}`, id);
      } else {
        localStorage.removeItem(`selectedImageId-${sessionId}`);
      }
    }
    setSelectedImageIdState(id);
  };

  // Image URL caching
  const [imageUrls, setImageUrls] = useState<Map<number, string>>(new Map());
  const [imageLoadingStatus, setImageLoadingStatus] = useState<Map<number, "loading" | "loaded" | "error">>(new Map());

  // UI state
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [verboseLogging] = useState(() => localStorage.getItem("verbose_logging") === "true");
  const [newlyCreatedDetectionId, setNewlyCreatedDetectionId] = useState<number | null>(null);
  const [failedDetectionNotification, setFailedDetectionNotification] = useState<{
    message: string;
    visible: boolean;
    classificationDetails?: any;
  } | null>(null);

  // Modal state
  const [showDirectCreateSectionModal, setShowDirectCreateSectionModal] = useState(false);
  const [imageToAssign, setImageToAssign] = useState<HistoryItem | null>(null);
  const [imageToAssignBase64, setImageToAssignBase64] = useState<string | null>(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationDialogResult, setValidationDialogResult] = useState<SectionValidationResult | null>(null);
  const [showSectionDetailsDialog, setShowSectionDetailsDialog] = useState(false);
  const [selectedSectionForDetails, setSelectedSectionForDetails] = useState<RoomSection | null>(null);

  // User corrections state
  const [userCorrections, setUserCorrections] = useState<Map<string, { detectionId: number; correctedClass: string; originalClass: string }>>(new Map());

  // Refs
  const settingsButtonRef = useRef<HTMLDivElement>(null);
  const settingsDialogRef = useRef<HTMLDivElement>(null);

  // ============================================
  // Computed Values
  // ============================================
  const isSessionCompleted = session?.status === "completed" || !session?.is_active;

  // Apply user corrections to analyses
  const correctedAnalyses = useMemo(() => {
    if (userCorrections.size === 0) return analyses;

    return analyses.map((analysis) => {
      if (!analysis.detections) return analysis;

      const correctedDetections = analysis.detections.map((detection) => {
        const correctionKey = `analysis-${analysis.id}-detection-${detection.id}`;
        const correction = userCorrections.get(correctionKey);
        if (!correction) return detection;

        const newTopClasses = [...(detection.top_classes || [])];
        const correctedClassIndex = newTopClasses.findIndex((cls) => cls.label === correction.correctedClass);
        if (correctedClassIndex > -1) {
          const correctedClass = newTopClasses.splice(correctedClassIndex, 1)[0];
          newTopClasses.unshift(correctedClass);
        }

        const isRealCorrection = correction.originalClass !== correction.correctedClass;
        return {
          ...detection,
          top_classes: newTopClasses,
          original_top1_class: correction.originalClass,
          user_corrected_class: isRealCorrection ? correction.correctedClass : undefined,
          is_user_corrected: true,
          is_confident: true,
          user_drawn: detection.user_drawn,
        };
      });

      return { ...analysis, detections: correctedDetections };
    });
  }, [analyses, userCorrections]);

  // Apply user corrections to uploaded images
  const correctedUploadedImages = useMemo(() => {
    if (userCorrections.size === 0) return uploadedImages;

    return uploadedImages.map((img) => {
      if (!img.result?.detections) return img;

      const correctedDetections = img.result.detections.map((detection: Detection) => {
        const correctionKey = `${img.id}-detection-${detection.id}`;
        const correction = userCorrections.get(correctionKey);
        if (!correction) return detection;

        const newTopClasses = [...(detection.top_classes || [])];
        const correctedClassIndex = newTopClasses.findIndex((cls) => cls.label === correction.correctedClass);
        if (correctedClassIndex > -1) {
          const correctedClass = newTopClasses.splice(correctedClassIndex, 1)[0];
          newTopClasses.unshift(correctedClass);
        }

        const isRealCorrection = correction.originalClass !== correction.correctedClass;
        return {
          ...detection,
          top_classes: newTopClasses,
          original_top1_class: correction.originalClass,
          user_corrected_class: isRealCorrection ? correction.correctedClass : undefined,
          is_user_corrected: true,
          is_confident: true,
          user_drawn: detection.user_drawn,
        };
      });

      return { ...img, result: { ...img.result, detections: correctedDetections } };
    });
  }, [uploadedImages, userCorrections]);

  // Organize sections with status
  const sectionsWithStatus = useMemo(() => {
    return organizeSectionsWithStatus(sections, correctedAnalyses);
  }, [sections, correctedAnalyses]);

  // Get unassigned images
  const unassignedImages = useMemo(() => {
    return correctedAnalyses.filter((analysis) => analysis.section_id === null || analysis.section_id === undefined);
  }, [correctedAnalyses]);

  // Create validation status map
  const classValidationMap = useMemo(() => {
    if (!validationResult) return undefined;
    const map = new Map<string, "match" | "mismatch" | "unexpected">();
    validationResult.validation_items.forEach((item) => {
      if (item.status === "match") map.set(item.class_name, "match");
      else if (item.status === "mismatch") map.set(item.class_name, "mismatch");
      else if (item.status === "unexpected") map.set(item.class_name, "unexpected");
    });
    return map;
  }, [validationResult]);

  // Check session readiness
  const isSessionReady = useMemo(() => {
    if (!initialLoadComplete || loadingSections || loading) return false;

    const hasValidationIssues = sectionsWithStatus.some(({ status }) => status === "mismatch" || status === "empty");
    if (hasValidationIssues) return false;

    // Check for uncertain detections
    for (const analysis of correctedAnalyses) {
      if (analysis.detections) {
        const hasUnhandledUncertain = analysis.detections.some((detection) => {
          if (detection.user_drawn) {
            return !detection.is_user_corrected && (!detection.is_confident || detection.passes_cutoff === false || detection.passes_refiner === false);
          }
          return (!detection.is_confident || detection.passes_cutoff === false || detection.passes_refiner === false) && !detection.is_user_corrected;
        });
        if (hasUnhandledUncertain) return false;
      }
    }

    return true;
  }, [initialLoadComplete, loadingSections, loading, sectionsWithStatus, correctedAnalyses]);

  const hasImages = correctedAnalyses.length > 0 || correctedUploadedImages.filter((img) => img.result).length > 0;

  // Check if current image has uncertain detections
  const hasUncertainDetectionsInCurrentImage = useMemo(() => {
    if (!selectedImageId) return false;

    if (selectedImageId.startsWith("analysis-")) {
      const analysisId = parseInt(selectedImageId.replace("analysis-", ""));
      const analysis = correctedAnalyses.find((a) => a.id === analysisId);
      if (analysis?.detections) {
        return analysis.detections.some((detection) => {
          if (detection.user_drawn) {
            return !detection.is_user_corrected && (!detection.is_confident || detection.passes_cutoff === false || detection.passes_refiner === false);
          }
          return (!detection.is_confident || detection.passes_cutoff === false || detection.passes_refiner === false) && !detection.is_user_corrected;
        });
      }
    }

    return false;
  }, [selectedImageId, correctedAnalyses]);

  // Section navigation
  const getCurrentSectionIndex = useCallback(() => {
    if (!selectedImageId) return -1;
    for (let i = 0; i < sectionsWithStatus.length; i++) {
      const { images } = sectionsWithStatus[i];
      if (images.some((img) => `analysis-${img.id}` === selectedImageId)) {
        return i;
      }
    }
    return -1;
  }, [selectedImageId, sectionsWithStatus]);

  const hasPrevImage = getCurrentSectionIndex() > 0;
  const hasNextImage = getCurrentSectionIndex() >= 0 && getCurrentSectionIndex() < sectionsWithStatus.length - 1;

  // ============================================
  // API Functions
  // ============================================
  const fetchSessionData = useCallback(async () => {
    if (!sessionId) return null;

    try {
      setLoading(true);
      const [sessionResponse, analysesResponse] = await Promise.all([
        apiService.getSession(parseInt(sessionId)),
        apiService.getSessionAnalyses(parseInt(sessionId), 1000, 0),
      ]);

      setSession(sessionResponse);
      setAnalyses(analysesResponse.analyses);
      return analysesResponse.analyses;
    } catch (error: any) {
      console.error("Failed to fetch session data:", error);
      setError("Failed to load session details");
      return null;
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const loadAnalysisImage = useCallback(async (analysisId: number): Promise<string | null> => {
    if (analysisId < 0) return null;
    if (imageUrls.has(analysisId)) return imageUrls.get(analysisId)!;
    if (imageLoadingStatus.get(analysisId) === "loading") {
      return new Promise<string | null>((resolve) => {
        const checkStatus = () => {
          const status = imageLoadingStatus.get(analysisId);
          if (status === "loaded") resolve(imageUrls.get(analysisId)!);
          else if (status === "error") resolve(null);
          else setTimeout(checkStatus, 50);
        };
        checkStatus();
      });
    }

    try {
      setImageLoadingStatus((prev) => new Map(prev).set(analysisId, "loading"));
      const blob = await apiService.getAnalysisImageBlob(analysisId);
      const url = URL.createObjectURL(blob);
      setImageUrls((prev) => new Map(prev).set(analysisId, url));
      setImageLoadingStatus((prev) => new Map(prev).set(analysisId, "loaded"));
      return url;
    } catch (error) {
      console.error(`Failed to load image for analysis ${analysisId}:`, error);
      setImageLoadingStatus((prev) => new Map(prev).set(analysisId, "error"));
      return null;
    }
  }, [imageUrls, imageLoadingStatus]);

  const saveSessionSettings = useCallback(async (newSettings?: any) => {
    if (!sessionId) return;
    const settingsToSave = newSettings || settings;

    try {
      setSaveStatus("saving");
      await apiService.saveSessionSettings(parseInt(sessionId), {
        conf_threshold: settingsToSave.conf_threshold,
        iou_threshold: settingsToSave.iou_threshold,
        yolo_img_size: settingsToSave.yolo_img_size,
        cutoff_threshold: settingsToSave.cutoff_threshold,
        top_distances: settingsToSave.top_distances,
        major_votes: settingsToSave.major_votes,
        show_uncertain_detections: settingsToSave.show_uncertain_detections,
        refiner_enabled: settingsToSave.refiner_enabled,
        refiner_threshold: settingsToSave.refiner_threshold,
        refiner_trigger_threshold: settingsToSave.refiner_trigger_threshold,
        disable_manual_detection_discard: settingsToSave.disable_manual_detection_discard,
      });
      setSaveStatus("saved");
      setHasUnsavedChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (error: any) {
      console.error("Failed to save session settings:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  }, [sessionId, settings]);

  // ============================================
  // Event Handlers
  // ============================================
  const handlePrevImage = useCallback(() => {
    const currentSectionIndex = getCurrentSectionIndex();
    if (currentSectionIndex > 0) {
      const prevSection = sectionsWithStatus[currentSectionIndex - 1];
      if (prevSection.images.length > 0) {
        setSelectedImageId(`analysis-${prevSection.images[0].id}`);
      }
    }
  }, [getCurrentSectionIndex, sectionsWithStatus]);

  const handleNextImage = useCallback(() => {
    const currentSectionIndex = getCurrentSectionIndex();
    if (currentSectionIndex >= 0 && currentSectionIndex < sectionsWithStatus.length - 1) {
      const nextSection = sectionsWithStatus[currentSectionIndex + 1];
      if (nextSection.images.length > 0) {
        setSelectedImageId(`analysis-${nextSection.images[0].id}`);
      }
    }
  }, [getCurrentSectionIndex, sectionsWithStatus]);

  const handleEmptySectionClick = useCallback((sectionId: number) => {
    setSelectedSectionId(sectionId);
    setTimeout(() => {
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
        fileInput.click();
      }
    }, 0);
  }, []);

  const handleSectionClick = useCallback((sectionId: number, images: HistoryItem[]) => {
    if (images.length > 0) {
      setSelectedImageId(`analysis-${images[0].id}`);
    }
  }, []);

  const handleViewValidation = useCallback(async (sectionId: number, images: HistoryItem[]) => {
    if (images.length === 0) return;
    const firstImageId = images[0].id;

    try {
      const validation = await apiService.validateSectionAnalysis(sectionId, firstImageId);
      setValidationDialogResult(validation);
      setShowValidationDialog(true);
    } catch (error) {
      console.error("Failed to fetch validation:", error);
      alert("Failed to load validation results. Please try again.");
    }
  }, []);

  const handleViewSectionDetails = useCallback((section: RoomSection) => {
    setSelectedSectionForDetails(section);
    setShowSectionDetailsDialog(true);
  }, []);

  const handleSectionAssignment = useCallback(async (analysisId: number, sectionId: number | null) => {
    try {
      await apiService.updateAnalysisSection(analysisId, sectionId);
      setAnalyses((prev) => prev.map((analysis) => (analysis.id === analysisId ? { ...analysis, section_id: sectionId } : analysis)));
    } catch (error) {
      console.error("Failed to assign section:", error);
      throw error;
    }
  }, []);

  const handleDeleteAnalysis = useCallback(async (analysisId: number) => {
    if (!confirm("Are you sure you want to delete this analysis?")) return;
    try {
      await apiService.deleteAnalysis(analysisId);
      setAnalyses((prev) => prev.filter((analysis) => analysis.id !== analysisId));
      // Clear selection if deleted analysis was selected
      if (selectedImageId === `analysis-${analysisId}`) {
        setSelectedImageId(null);
      }
    } catch (error) {
      console.error("Failed to delete analysis:", error);
      alert("Failed to delete analysis. Please try again.");
    }
  }, [selectedImageId]);

  const handleCreateSection = useCallback(async (sectionData: Parameters<typeof apiService.createSection>[0]) => {
    // Use session-based API if we have a session ID (doesn't require admin)
    // Otherwise fall back to admin-only API
    let newSection;
    if (sessionId) {
      newSection = await apiService.createSectionForSession({
        session_id: parseInt(sessionId),
        name: sectionData.name,
        description: sectionData.description,
        image_path: sectionData.image_path,
        expected_items: sectionData.expected_items,
      });
    } else {
      newSection = await apiService.createSection(sectionData);
    }
    // Add the new section to the sections list
    setSections((prev) => [...prev, newSection]);
    return newSection;
  }, [sessionId]);

  // Extract detected classes from imageToAssign for CreateSectionModal
  const detectedClassesFromImage = useMemo(() => {
    if (!imageToAssign?.detections) return [];
    const classMap = new Map<string, number>();
    imageToAssign.detections.forEach(detection => {
      const className = detection.user_corrected_class ||
        (detection.top_classes && detection.top_classes.length > 0
          ? (detection.top_classes[0].class_name || detection.top_classes[0].label)
          : null);
      if (className) {
        classMap.set(className, (classMap.get(className) || 0) + 1);
      }
    });
    return Array.from(classMap.entries()).map(([className, count]) => ({
      class_name: className,
      expected_quantity: count
    }));
  }, [imageToAssign]);

  // Fetch image base64 when modal opens with an image to assign
  useEffect(() => {
    if (showDirectCreateSectionModal && imageToAssign) {
      apiService.getAnalysisImageBlob(imageToAssign.id)
        .then(async (blob) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onload = () => setImageToAssignBase64(reader.result as string);
        })
        .catch(err => console.error('Failed to load image for section:', err));
    } else {
      setImageToAssignBase64(null);
    }
  }, [showDirectCreateSectionModal, imageToAssign]);

  // Handle direct section creation success
  const handleDirectCreateSectionSuccess = useCallback(async (newSection: RoomSection) => {
    // Assign the image to the newly created section
    if (imageToAssign) {
      await apiService.updateAnalysisSection(imageToAssign.id, newSection.id);
      setAnalyses((prev) => prev.map((analysis) =>
        analysis.id === imageToAssign.id ? { ...analysis, section_id: newSection.id } : analysis
      ));
    }
    setShowDirectCreateSectionModal(false);
    setImageToAssign(null);
    setImageToAssignBase64(null);
  }, [imageToAssign]);

  const handleStageChanges = useCallback(async () => {
    if (!session || !sessionId) return;
    if (!initialLoadComplete || loadingSections || loading) {
      alert("Please wait for all data to finish loading before completing the session.");
      return;
    }

    const sectionsWithIssues = sectionsWithStatus.filter(({ status }) => status === "mismatch" || status === "empty");
    if (sectionsWithIssues.length > 0) {
      const issueDetails = sectionsWithIssues
        .map(({ section, status }) => `• ${section.name}: ${status === "empty" ? "No images uploaded" : "Validation issues detected"}`)
        .join("\n");
      alert(`Cannot complete session. The following sections have issues:\n\n${issueDetails}\n\nPlease resolve all validation issues before completing the session.`);
      return;
    }

    const confirmed = window.confirm("Are you sure you have finished all the work on this session? This will mark the session as completed and prevent further modifications.");
    if (confirmed) {
      try {
        await apiService.updateSessionStatus(parseInt(sessionId), { is_active: false, status: "completed" });
        setSession((prev) => (prev ? { ...prev, is_active: false, status: "completed" } : null));
      } catch (error: any) {
        console.error("Failed to complete session:", error);
        alert(t("session_details.stage_failed"));
      }
    }
  }, [session, sessionId, initialLoadComplete, loadingSections, loading, sectionsWithStatus, t]);

  const handleDeleteUncertainDetections = useCallback(async () => {
    if (!selectedImageId || !sessionId) return;

    const confirmed = window.confirm("Are you sure you want to delete all uncertain detections from this image? This action cannot be undone.");
    if (!confirmed) return;

    try {
      const analysisId = parseInt(selectedImageId.replace("analysis-", ""));
      const analysis = correctedAnalyses.find((a) => a.id === analysisId);
      if (!analysis?.detections) return;

      const uncertainDetections = analysis.detections.filter((detection) => {
        if (detection.user_drawn) {
          return !detection.is_user_corrected && (!detection.is_confident || detection.passes_cutoff === false || detection.passes_refiner === false);
        }
        return (!detection.is_confident || detection.passes_cutoff === false || detection.passes_refiner === false) && !detection.is_user_corrected;
      });

      for (const detection of uncertainDetections) {
        await apiService.deleteDetection(parseInt(sessionId), analysisId, detection.id);
        setAnalyses((prev) =>
          prev.map((a) => {
            if (a.id === analysisId) {
              const updatedDetections = (a.detections || []).filter((d) => d.id !== detection.id);
              return { ...a, detections: updatedDetections, total_detections: updatedDetections.length };
            }
            return a;
          })
        );
      }
    } catch (error: any) {
      console.error("Failed to delete uncertain detections:", error);
      alert("Failed to delete uncertain detections. Please try again.");
    }
  }, [selectedImageId, sessionId, correctedAnalyses]);

  const handleGenerateReport = useCallback(() => {
    if (session) {
      const date = new Date(session.created_at).toISOString().split("T")[0];
      const roomId = session.room_id;
      const params = new URLSearchParams();
      params.set('date', date);
      if (roomId) params.set('rooms', roomId.toString());
      navigate(`/reports?${params.toString()}`);
    } else {
      navigate('/reports');
    }
  }, [session, navigate]);

  const handleCloseSettings = useCallback(async () => {
    if (hasUnsavedChanges && sessionId) {
      await saveSessionSettings();
    }
    setShowSettings(false);
    setHasUnsavedChanges(false);
  }, [hasUnsavedChanges, sessionId, saveSessionSettings]);

  const handleClassOverride = useCallback(async (detectionId: number, newTopClass: string, _newScore: number) => {
    if (!sessionId || !selectedImageId) return;

    const analysisId = selectedImageId.startsWith("analysis-") ? parseInt(selectedImageId.replace("analysis-", "")) : null;
    if (!analysisId) return;

    const analysis = analyses.find((a) => a.id === analysisId);
    const currentDetection = analysis?.detections?.find((d) => d.id === detectionId);
    if (!currentDetection) return;

    const originalTop1Class = currentDetection.original_top1_class || currentDetection.top_classes?.[0]?.label || "unknown";
    const isRealCorrection = newTopClass !== originalTop1Class;
    const correctionKey = `analysis-${analysisId}-detection-${detectionId}`;
    const existingCorrection = userCorrections.get(correctionKey);

    // Check if detection is uncertain (fails any threshold)
    const isUncertain = currentDetection.passes_cutoff === false || currentDetection.passes_refiner === false || !currentDetection.is_confident;

    try {
      if (isRealCorrection) {
        // Real correction - save it
        await apiService.saveUserCorrection(parseInt(sessionId), {
          image_key: selectedImageId,
          detection_id: detectionId,
          original_class: originalTop1Class,
          corrected_class: newTopClass,
          analysis_id: analysisId,
        });
      } else if (existingCorrection && !isUncertain) {
        // Reverting a confident detection - delete the correction
        await apiService.deleteUserCorrection(parseInt(sessionId), selectedImageId, detectionId);
      } else if (isUncertain) {
        // Confirming an uncertain detection (accepting without changing class) - save it
        await apiService.saveUserCorrection(parseInt(sessionId), {
          image_key: selectedImageId,
          detection_id: detectionId,
          original_class: originalTop1Class,
          corrected_class: newTopClass,
          analysis_id: analysisId,
        });
      }

      const newTopClasses = [...(currentDetection.top_classes || [])];
      if (isRealCorrection) {
        const selectedClassIndex = newTopClasses.findIndex((cls) => cls.label === newTopClass);
        if (selectedClassIndex > -1) {
          const selectedClass = newTopClasses.splice(selectedClassIndex, 1)[0];
          newTopClasses.unshift(selectedClass);
        }
      }

      const isUserCorrection = isRealCorrection || isUncertain;

      const updatedDetection: Detection = {
        ...currentDetection,
        top_classes: newTopClasses,
        is_confident: true,
        original_top1_class: originalTop1Class,
        user_corrected_class: isUserCorrection ? newTopClass : undefined,
        is_user_corrected: isUserCorrection,
      };

      const newCorrections = new Map(userCorrections);
      if (isUserCorrection) {
        newCorrections.set(correctionKey, { detectionId, correctedClass: newTopClass, originalClass: originalTop1Class });
      } else {
        newCorrections.delete(correctionKey);
      }
      setUserCorrections(newCorrections);

      setAnalyses((prev) =>
        prev.map((a) => {
          if (a.id === analysisId && a.detections) {
            return { ...a, detections: a.detections.map((d) => (d.id === detectionId ? updatedDetection : d)) };
          }
          return a;
        })
      );
    } catch (error) {
      console.error("Failed to save correction:", error);
      alert(t("session_details.failed_save_correction"));
    }
  }, [sessionId, selectedImageId, analyses, userCorrections, t]);

  const handleUserDrawnDetection = useCallback(async (bbox: [number, number, number, number], imageElement: HTMLImageElement) => {
    if (!sessionId || !selectedImageId) return;

    const tempId = -Date.now();
    const tempDetection: Detection = {
      id: tempId,
      bbox: bbox,
      confidence: 1.0,
      is_confident: false,
      is_user_corrected: false,
      class_scores: {},
      top_classes: [],
      original_top1_class: "Classifying...",
      user_drawn: true,
    };

    const analysisId = selectedImageId.startsWith("analysis-") ? parseInt(selectedImageId.replace("analysis-", "")) : null;
    if (analysisId) {
      setAnalyses((prev) =>
        prev.map((analysis) => {
          if (analysis.id === analysisId) {
            return {
              ...analysis,
              detections: [...(analysis.detections || []), tempDetection],
              total_detections: (analysis.total_detections || 0) + 1,
            };
          }
          return analysis;
        })
      );
    }

    try {
      const [x1, y1, x2, y2] = bbox;
      const scaleX = imageElement.clientWidth / imageElement.naturalWidth;
      const scaleY = imageElement.clientHeight / imageElement.naturalHeight;
      const displayX = x1 * scaleX;
      const displayY = y1 * scaleY;
      const displayRectWidth = (x2 - x1) * scaleX;
      const displayRectHeight = (y2 - y1) * scaleY;

      const result = await apiService.createUserDetection(parseInt(sessionId), {
        image_key: selectedImageId,
        bbox: bbox,
        display_coords: [displayX, displayY, displayRectWidth, displayRectHeight],
        image_dimensions: {
          naturalWidth: imageElement.naturalWidth,
          naturalHeight: imageElement.naturalHeight,
          clientWidth: imageElement.clientWidth,
          clientHeight: imageElement.clientHeight,
        },
      });

      if (result.success === false && result.user_drawn_failed === true) {
        if (analysisId) {
          setAnalyses((prev) =>
            prev.map((analysis) => {
              if (analysis.id === analysisId) {
                const updatedDetections = (analysis.detections || []).filter((d) => d.id !== tempId);
                return { ...analysis, detections: updatedDetections, total_detections: updatedDetections.length };
              }
              return analysis;
            })
          );
        }
        setIsDrawingMode(false);
        setFailedDetectionNotification({
          message: result.message || "Selected area has no valid object",
          visible: true,
          classificationDetails: result.classification_details,
        });
        setTimeout(() => {
          setFailedDetectionNotification((prev) => (prev ? { ...prev, visible: false } : null));
          setTimeout(() => setFailedDetectionNotification(null), 300);
        }, 4000);
        return;
      }

      const detectionId = result.detection?.id || result.detection_id || result.id || Date.now();
      const userDetection: Detection = {
        id: detectionId,
        bbox: result.detection?.bbox || bbox,
        confidence: result.detection?.confidence || 1.0,
        is_confident: result.detection?.is_confident || false,
        is_user_corrected: false,
        class_scores: result.detection?.class_scores || {},
        top_classes: result.detection?.top_classes || [],
        original_top1_class: result.detection?.original_top1_class || "unknown",
        user_drawn: true,
        quality_score: result.detection?.quality_score,
        passes_cutoff: result.detection?.passes_cutoff,
        passes_refiner: result.detection?.passes_refiner,
      };

      if (analysisId) {
        setAnalyses((prev) =>
          prev.map((analysis) => {
            if (analysis.id === analysisId) {
              const updatedDetections = (analysis.detections || []).map((d) => (d.id === tempId ? userDetection : d));
              return { ...analysis, detections: updatedDetections };
            }
            return analysis;
          })
        );
      }

      setIsDrawingMode(false);
      setNewlyCreatedDetectionId(userDetection.id);
    } catch (error) {
      console.error("Failed to create user detection:", error);
      if (analysisId) {
        setAnalyses((prev) =>
          prev.map((analysis) => {
            if (analysis.id === analysisId) {
              const updatedDetections = (analysis.detections || []).filter((d) => d.id !== tempId);
              return { ...analysis, detections: updatedDetections, total_detections: updatedDetections.length };
            }
            return analysis;
          })
        );
      }
      alert(t("session_details.failed_create_detection"));
    }
  }, [sessionId, selectedImageId, t]);

  const handleDeleteDetection = useCallback(async (detectionId: number) => {
    if (!sessionId || !selectedImageId) return;

    const analysisId = selectedImageId.startsWith("analysis-") ? parseInt(selectedImageId.replace("analysis-", "")) : null;
    if (!analysisId) return;

    try {
      await apiService.deleteDetection(parseInt(sessionId), analysisId, detectionId);
      setAnalyses((prev) =>
        prev.map((analysis) => {
          if (analysis.id === analysisId) {
            const updatedDetections = (analysis.detections || []).filter((d) => d.id !== detectionId);
            return { ...analysis, detections: updatedDetections, total_detections: updatedDetections.length };
          }
          return analysis;
        })
      );
    } catch (error) {
      console.error("Failed to delete detection:", error);
      alert(t("session_details.delete_detection_confirmation"));
    }
  }, [sessionId, selectedImageId, t]);

  // ============================================
  // Mobile-specific Handlers
  // ============================================
  const handleMobileUploadComplete = useCallback(
    (result: any, sectionId: number | null) => {
      console.log('[MobileUpload] handleMobileUploadComplete called', { analysis_id: result.analysis_id, sectionId });
      if (result.analysis_id) {
        // Add the new analysis to the list
        const newAnalysis: HistoryItem = {
          id: result.analysis_id,
          image_name: result.image_name || 'uploaded_image.jpg',
          processing_time: result.processing_time || 0,
          total_detections: result.total_detections || 0,
          detections: result.detections || [],
          timestamp: new Date().toISOString(),
          section_id: sectionId,
        };
        console.log('[MobileUpload] Adding analysis to state', { newAnalysis });
        setAnalyses((prev) => {
          console.log('[MobileUpload] Previous analyses count:', prev.length);
          return [newAnalysis, ...prev];
        });

        // Update session image count
        setSession((prev) =>
          prev ? { ...prev, image_count: (prev.image_count || 0) + 1 } : null
        );
      } else {
        console.warn('[MobileUpload] No analysis_id in result', result);
      }
    },
    []
  );

  const handleMobileClassCorrection = useCallback(
    async (detectionId: number, newClass: string) => {
      // Find the analysis containing this detection
      const analysisWithDetection = correctedAnalyses.find((a) =>
        a.detections?.some((d) => d.id === detectionId)
      );
      if (!analysisWithDetection) return;

      const detection = analysisWithDetection.detections?.find(
        (d) => d.id === detectionId
      );
      if (!detection) return;

      // Use the existing handleClassOverride logic
      const originalTop1Class =
        detection.original_top1_class ||
        detection.top_classes?.[0]?.label ||
        'unknown';
      const isRealCorrection = newClass !== originalTop1Class;
      const correctionKey = `analysis-${analysisWithDetection.id}-detection-${detectionId}`;
      const existingCorrection = userCorrections.get(correctionKey);

      // Check if detection is uncertain (fails any threshold)
      const isUncertain =
        detection.passes_cutoff === false ||
        detection.passes_refiner === false ||
        !detection.is_confident;

      try {
        if (isRealCorrection && sessionId) {
          // Real correction - save it
          await apiService.saveUserCorrection(parseInt(sessionId), {
            image_key: `analysis-${analysisWithDetection.id}`,
            detection_id: detectionId,
            original_class: originalTop1Class,
            corrected_class: newClass,
            analysis_id: analysisWithDetection.id,
          });
        } else if (existingCorrection && !isUncertain && sessionId) {
          // Reverting a confident detection - delete the correction
          await apiService.deleteUserCorrection(parseInt(sessionId), `analysis-${analysisWithDetection.id}`, detectionId);
        } else if (isUncertain && sessionId) {
          // Confirming an uncertain detection (accepting without changing class) - save it
          await apiService.saveUserCorrection(parseInt(sessionId), {
            image_key: `analysis-${analysisWithDetection.id}`,
            detection_id: detectionId,
            original_class: originalTop1Class,
            corrected_class: newClass,
            analysis_id: analysisWithDetection.id,
          });
        }

        const newTopClasses = [...(detection.top_classes || [])];
        if (isRealCorrection) {
          const selectedClassIndex = newTopClasses.findIndex(
            (cls) => cls.label === newClass
          );
          if (selectedClassIndex > -1) {
            const selectedClass = newTopClasses.splice(selectedClassIndex, 1)[0];
            newTopClasses.unshift(selectedClass);
          }
        }

        const isUserCorrection = isRealCorrection || isUncertain;

        const updatedDetection: Detection = {
          ...detection,
          top_classes: newTopClasses,
          is_confident: true,
          original_top1_class: originalTop1Class,
          user_corrected_class: isUserCorrection ? newClass : undefined,
          is_user_corrected: isUserCorrection,
        };

        const newCorrections = new Map(userCorrections);
        if (isUserCorrection) {
          newCorrections.set(correctionKey, {
            detectionId,
            correctedClass: newClass,
            originalClass: originalTop1Class,
          });
        } else {
          newCorrections.delete(correctionKey);
        }
        setUserCorrections(newCorrections);

        setAnalyses((prev) =>
          prev.map((a) => {
            if (a.id === analysisWithDetection.id && a.detections) {
              return {
                ...a,
                detections: a.detections.map((d) =>
                  d.id === detectionId ? updatedDetection : d
                ),
              };
            }
            return a;
          })
        );
      } catch (error) {
        console.error('Failed to save correction:', error);
      }
    },
    [correctedAnalyses, sessionId, userCorrections]
  );

  const handleMobileSessionComplete = useCallback(async () => {
    if (!session || !sessionId) return;

    try {
      await apiService.updateSessionStatus(parseInt(sessionId), {
        is_active: false,
        status: 'completed',
      });
      setSession((prev) =>
        prev ? { ...prev, is_active: false, status: 'completed' } : null
      );
    } catch (error: any) {
      console.error('Failed to complete session:', error);
      alert('Failed to complete session. Please try again.');
    }
  }, [session, sessionId]);

  const handleNavigateBack = useCallback(() => {
    navigate('/sessions');
  }, [navigate]);

  // ============================================
  // Dropzone Configuration
  // ============================================
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!session) {
      console.warn("Upload attempted before session loaded - this should not happen");
      return;
    }
    if (acceptedFiles.length === 0) return; // No valid files accepted

    const newImages = acceptedFiles.map((file, index) => {
      const tempId = -Date.now() - index;
      return {
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: URL.createObjectURL(file),
        tempAnalysisId: tempId,
      };
    });

    const tempAnalyses: HistoryItem[] = newImages.map((img) => ({
      id: img.tempAnalysisId!,
      image_name: img.file.name,
      processing_time: 0,
      total_detections: 0,
      detections: [],
      timestamp: new Date().toISOString(),
      section_id: selectedSectionId,
      isProcessing: true,
      uploadData: { fileId: img.id, file: img.file, preview: img.preview },
    }));

    setAnalyses((prev) => [...tempAnalyses, ...prev]);
    setUploadedImages((prev) => [...prev, ...newImages]);

    const isMultiple = newImages.length > 1;
    if (isMultiple) {
      setIsAnalyzingMultiple(true);
      setAnalysisProgress({ current: 0, total: newImages.length });
      setProcessedImages([]);
    }

    const mostRecentImage = newImages[newImages.length - 1];
    setSelectedImageId(`analysis-${mostRecentImage.tempAnalysisId}`);

    let successfulAnalyses = 0;
    let lastProcessedAnalysisId: number | null = null;
    let lastProcessedResult: any = null;

    for (let i = 0; i < newImages.length; i++) {
      const image = newImages[i];

      if (isMultiple) {
        setAnalysisProgress({ current: i + 1, total: newImages.length });
      }

      setUploadedImages((prev) => prev.map((img) => (img.id === image.id ? { ...img, isAnalyzing: true, error: undefined } : img)));

      try {
        const result = await apiService.analyzeImage(image.file, {
          session_id: session.id!,
          section_id: selectedSectionId || undefined,
          confidence_threshold: settings.conf_threshold,
          k: settings.k,
          show_uncertain_detections: settings.show_uncertain_detections,
        });

        if (isMultiple) {
          setProcessedImages((prev) => [...prev, image.file.name]);
        }

        setUploadedImages((prev) => prev.map((img) => (img.id === image.id ? { ...img, result, isAnalyzing: false } : img)));
        successfulAnalyses++;

        if (result.analysis_id) {
          lastProcessedAnalysisId = result.analysis_id;
          lastProcessedResult = result;
          const wasSelected = selectedImageId === `analysis-${image.tempAnalysisId}`;

          setAnalyses((prev) =>
            prev.map((analysis) => {
              if (analysis.id === image.tempAnalysisId) {
                return {
                  ...analysis,
                  id: result.analysis_id!,
                  total_detections: result.total_detections,
                  detections: result.detections,
                  processing_time: result.processing_time,
                  section_id: selectedSectionId,
                  isProcessing: false,
                  uploadData: undefined,
                };
              }
              return analysis;
            })
          );

          if (wasSelected) {
            setSelectedImageId(`analysis-${result.analysis_id}`);
          }
        }

        setTimeout(() => {
          setUploadedImages((prev) => prev.filter((img) => img.id !== image.id));
        }, 100);
      } catch (error: any) {
        console.error(`Analysis failed:`, error);
        setUploadedImages((prev) =>
          prev.map((img) =>
            img.id === image.id ? { ...img, error: error.response?.data?.detail || "Analysis failed", isAnalyzing: false } : img
          )
        );
      }
    }

    if (isMultiple) {
      setTimeout(() => {
        setIsAnalyzingMultiple(false);
        setAnalysisProgress({ current: 0, total: 0 });
        setProcessedImages([]);

        if (successfulAnalyses > 0 && session) {
          setSession((prev) => (prev ? { ...prev, image_count: (prev.image_count || 0) + successfulAnalyses } : null));
          if (lastProcessedAnalysisId) {
            setSelectedImageId(`analysis-${lastProcessedAnalysisId}`);
          }
        }
      }, 1500);
    } else if (successfulAnalyses > 0) {
      setSession((prev) => (prev ? { ...prev, image_count: (prev.image_count || 0) + successfulAnalyses } : null));
      if (lastProcessedAnalysisId) {
        setSelectedImageId(`analysis-${lastProcessedAnalysisId}`);

        // Image uploaded without section goes to unassigned - user can review detections
        // and then click to assign/create section when ready
      }
    }
  }, [session, settings.conf_threshold, settings.k, settings.show_uncertain_detections, selectedSectionId, selectedImageId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: (fileRejections) => {
      const errors = fileRejections.map((rejection) => {
        const errorCodes = rejection.errors.map((e) => e.code);
        if (errorCodes.includes("file-too-large")) {
          return `${rejection.file.name}: File too large (max 10MB)`;
        }
        if (errorCodes.includes("file-invalid-type")) {
          return `${rejection.file.name}: Invalid file type`;
        }
        return `${rejection.file.name}: Upload rejected`;
      });
      console.warn("Files rejected:", errors);
      alert(errors.join("\n"));
    },
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".webp", ".heic", ".heif"] },
    multiple: true,
    maxSize: 10 * 1024 * 1024,
    // Disable uploads until session is loaded to prevent silent failures on mobile
    disabled: loading || !session,
  });

  // ============================================
  // Effects
  // ============================================

  // Load default settings
  useEffect(() => {
    const loadDefaultSettings = async () => {
      try {
        const defaultResponse = await apiService.getDefaultSettings();
        const defaultSettings = {
          confidence_threshold: defaultResponse.conf_threshold,
          k: 5,
          topK: 5,
          conf_threshold: defaultResponse.conf_threshold,
          iou_threshold: defaultResponse.iou_threshold,
          yolo_img_size: defaultResponse.yolo_img_size,
          cutoff_threshold: defaultResponse.cutoff_threshold,
          top_distances: defaultResponse.top_distances ?? 15,
          major_votes: defaultResponse.major_votes ?? 5,
          show_uncertain_detections: defaultResponse.show_uncertain_detections,
          refiner_enabled: defaultResponse.refiner_enabled ?? true,
          refiner_threshold: defaultResponse.refiner_threshold ?? 0.5,
          refiner_trigger_threshold: defaultResponse.refiner_trigger_threshold ?? 1.0,
          disable_manual_detection_discard: defaultResponse.disable_manual_detection_discard ?? false,
        };
        setSettings(defaultSettings);
        setOriginalSettings(defaultSettings);
        setDefaultSettingsLoaded(true);
      } catch (error: any) {
        console.error("Failed to load default settings:", error);
        setDefaultSettingsLoaded(true);
      }
    };
    loadDefaultSettings();
  }, []);

  // Load session data after default settings
  useEffect(() => {
    if (!defaultSettingsLoaded) return;

    const loadSessionData = async () => {
      setInitialLoadComplete(false);
      await fetchSessionData();

      if (sessionId) {
        try {
          const settingsResponse = await apiService.getSessionSettings(parseInt(sessionId));
          const newSettings = {
            ...settings,
            conf_threshold: settingsResponse.conf_threshold,
            iou_threshold: settingsResponse.iou_threshold,
            yolo_img_size: settingsResponse.yolo_img_size,
            cutoff_threshold: settingsResponse.cutoff_threshold,
            top_distances: settingsResponse.top_distances ?? 15,
            major_votes: settingsResponse.major_votes ?? 5,
            show_uncertain_detections: settingsResponse.show_uncertain_detections,
            refiner_enabled: settingsResponse.refiner_enabled ?? true,
            refiner_threshold: settingsResponse.refiner_threshold ?? 0.5,
            refiner_trigger_threshold: settingsResponse.refiner_trigger_threshold ?? 1.0,
            disable_manual_detection_discard: settingsResponse.disable_manual_detection_discard ?? false,
            confidence_threshold: settingsResponse.conf_threshold,
          };
          setSettings(newSettings);
          setOriginalSettings(newSettings);
        } catch (error: any) {
          console.error("Failed to load session settings:", error);
        }
      }
    };
    loadSessionData();
  }, [sessionId, defaultSettingsLoaded]);

  // Load sections when session/room changes
  useEffect(() => {
    const loadSections = async () => {
      if (!session?.room_id) {
        setSections([]);
        setSelectedSectionId(null);
        return;
      }

      try {
        setLoadingSections(true);
        const roomSections = await apiService.getRoomSections(session.room_id);
        setSections(roomSections);
      } catch (error) {
        console.error("Failed to load sections:", error);
        setSections([]);
      } finally {
        setLoadingSections(false);
        setInitialLoadComplete(true);
      }
    };
    loadSections();
  }, [session?.room_id]);

  // Preload validation data for all sections with images
  useEffect(() => {
    const preloadValidationData = async () => {
      const sectionsToLoad: { sectionId: number; analysisId: number }[] = [];

      sectionsWithStatus.forEach((sectionData) => {
        if (sectionData.images && sectionData.images.length > 0) {
          const sectionId = sectionData.section.id;
          const latestImage = sectionData.images[0];
          if (!loadedValidationSectionIds.current.has(sectionId)) {
            sectionsToLoad.push({ sectionId, analysisId: latestImage.id });
            loadedValidationSectionIds.current.add(sectionId);
          }
        }
      });

      if (sectionsToLoad.length === 0) return;

      const results = await Promise.all(
        sectionsToLoad.map(async ({ sectionId, analysisId }) => {
          try {
            const validation = await apiService.validateSectionAnalysis(sectionId, analysisId);
            return { sectionId, validation };
          } catch (error) {
            console.error(`Failed to preload validation for section ${sectionId}:`, error);
            return { sectionId, validation: null };
          }
        })
      );

      setValidationCache((prev) => {
        const newCache = { ...prev };
        results.forEach(({ sectionId, validation }) => {
          if (validation) {
            newCache[sectionId] = validation;
          }
        });
        return newCache;
      });
    };

    preloadValidationData();
  }, [sectionsWithStatus]);

  // Helper to get validation counts from cache
  // Counts actual items (not classes) - sums the detected/expected quantities
  const getValidationCounts = useCallback((sectionId: number): { match: number; mismatch: number; missing: number; unexpected: number } | null => {
    const validation = validationCache[sectionId];
    if (!validation) return null;

    const counts = { match: 0, mismatch: 0, missing: 0, unexpected: 0 };
    validation.validation_items.forEach((item) => {
      if (item.status === 'match') {
        counts.match += item.detected;
      } else if (item.status === 'mismatch') {
        counts.mismatch += item.detected;
      } else if (item.status === 'missing') {
        // Missing items weren't detected - count the expected quantity that's missing
        counts.missing += item.expected;
      } else if (item.status === 'unexpected') {
        counts.unexpected += item.detected;
      }
    });
    return counts;
  }, [validationCache]);

  // Load user corrections
  useEffect(() => {
    if (sessionId) {
      apiService.getUserCorrections(parseInt(sessionId))
        .then((response) => {
          const correctionsMap = new Map();
          response.corrections.forEach((correction: any) => {
            const correctionKey = `${correction.image_key}-detection-${correction.detection_id}`;
            correctionsMap.set(correctionKey, {
              detectionId: correction.detection_id,
              correctedClass: correction.corrected_class,
              originalClass: correction.original_class,
            });
          });
          setUserCorrections(correctionsMap);
        })
        .catch((error) => console.error("Failed to load user corrections:", error));
    }
  }, [sessionId]);

  // Auto-select first analysis if none selected
  useEffect(() => {
    if (analyses.length > 0 && !selectedImageId) {
      const sortedAnalyses = [...analyses].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setSelectedImageId(`analysis-${sortedAnalyses[0].id}`);
    }
  }, [analyses, selectedImageId]);

  // Auto-validate when viewing an image with section
  useEffect(() => {
    if (!selectedImageId || !selectedImageId.startsWith("analysis-")) {
      setValidationResult(null);
      return;
    }

    const analysisId = parseInt(selectedImageId.replace("analysis-", ""));
    if (analysisId < 0) {
      setValidationResult(null);
      return;
    }

    const analysis = analyses.find((a) => a.id === analysisId);
    if (!analysis || analysis.isProcessing) {
      setValidationResult(null);
      return;
    }

    if (analysis.section_id) {
      apiService.validateSectionAnalysis(analysis.section_id, analysisId)
        .then((validation) => setValidationResult(validation))
        .catch(() => setValidationResult(null));
    } else {
      setValidationResult(null);
    }
  }, [selectedImageId, analyses]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement as HTMLElement;
      const isInputActive = activeElement && (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA" || activeElement.contentEditable === "true");
      if (isInputActive) return;

      if (event.key === "ArrowLeft" && hasPrevImage) {
        event.preventDefault();
        handlePrevImage();
      } else if (event.key === "ArrowRight" && hasNextImage) {
        event.preventDefault();
        handleNextImage();
      } else if (event.key === "Escape" && showSettings) {
        handleCloseSettings();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [hasPrevImage, hasNextImage, handlePrevImage, handleNextImage, showSettings, handleCloseSettings]);

  // Click outside settings handler
  useEffect(() => {
    const handleClickOutside = async (event: MouseEvent) => {
      if (
        showSettings &&
        settingsDialogRef.current &&
        settingsButtonRef.current &&
        !settingsDialogRef.current.contains(event.target as Node) &&
        !settingsButtonRef.current.contains(event.target as Node)
      ) {
        await handleCloseSettings();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSettings, handleCloseSettings]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      imageUrls.forEach((url) => URL.revokeObjectURL(url));
      uploadedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    };
  }, []);

  // ============================================
  // Render
  // ============================================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-700">{error || "Session not found"}</p>
        </div>
        <div className="mt-6">
          <Link to="/sessions" className="btn-primary">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("session_details.back_to_sessions")}
          </Link>
        </div>
      </div>
    );
  }

  const completeCount = sectionsWithStatus.filter((s) => s.status === "complete").length;
  const mismatchCount = sectionsWithStatus.filter((s) => s.status === "mismatch").length;
  const emptyCount = sectionsWithStatus.filter((s) => s.status === "empty").length;

  // Mobile View
  if (isMobile) {
    return (
      <div className="h-[calc(100vh-4rem-5rem)] -mx-4 -mt-4">
        <MobileSessionView
          session={session}
          sections={sections}
          sectionsWithStatus={sectionsWithStatus}
          analyses={correctedAnalyses}
          isSessionCompleted={isSessionCompleted}
          isSessionReady={isSessionReady}
          isAdmin={isAdmin}
          loadingSections={loadingSections}
          settings={settings}
          classValidationStatus={classValidationMap || new Map()}
          onUploadComplete={handleMobileUploadComplete}
          onClassCorrection={handleMobileClassCorrection}
          onSessionComplete={handleMobileSessionComplete}
          onViewSectionDetails={handleViewSectionDetails}
          onNavigateBack={handleNavigateBack}
          onStartNewSession={handleNavigateBack}
          loadAnalysisImage={loadAnalysisImage}
          onCreateSection={handleCreateSection}
          onAssignImageToSection={handleSectionAssignment}
        />

        {/* Keep modals for mobile */}
        {showSectionDetailsDialog && selectedSectionForDetails && (
          <SectionDetailsDialog
            section={selectedSectionForDetails}
            onClose={() => {
              setShowSectionDetailsDialog(false);
              setSelectedSectionForDetails(null);
            }}
          />
        )}
      </div>
    );
  }

  // Desktop View
  return (
    <>
      <div className="h-[calc(100vh-8rem)] flex flex-col space-y-4 overflow-visible">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border px-4 py-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/sessions" className="btn-outline p-2">
                <ArrowLeft className="h-4 w-4" />
              </Link>
              <div className="flex items-center space-x-4">
                <div className="text-lg font-sans font-semibold text-gray-900">
                  {session.room_name || session.name || "Session"}
                </div>

                <div className="flex items-center space-x-3 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Package className="h-3 w-3 mr-1" />
                    <span>{sectionsWithStatus.length} sections</span>
                  </div>
                  {completeCount > 0 && (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      <span>{completeCount} complete</span>
                    </div>
                  )}
                  {mismatchCount > 0 && (
                    <div className="flex items-center text-orange-600">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      <span>{mismatchCount} issues</span>
                    </div>
                  )}
                  {emptyCount > 0 && (
                    <div className="flex items-center text-red-600">
                      <Upload className="h-3 w-3 mr-1" />
                      <span>{emptyCount} empty</span>
                    </div>
                  )}
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      isSessionCompleted ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {isSessionCompleted ? t("session_details.completed_status") : t("session_details.in_progress_status")}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex-1 flex justify-end items-center space-x-3">
              {/* Draw and Clear buttons - only show when session is not completed */}
              {!isSessionCompleted && (
                <>
                  <Tooltip content={isDrawingMode ? t("session_details.exit_drawing_mode") : t("session_details.draw_detection")} position="bottom">
                    <button
                      onClick={() => setIsDrawingMode(!isDrawingMode)}
                      className={cn(
                        "flex items-center justify-center w-10 h-10 rounded-lg border transition-all",
                        isDrawingMode ? "bg-blue-100 border-blue-300 text-blue-700 shadow-sm" : "bg-white border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700"
                      )}
                    >
                      <PenTool className="h-4 w-4" />
                    </button>
                  </Tooltip>

                  <Tooltip content={!hasUncertainDetectionsInCurrentImage ? "No uncertain detections in current image" : "Delete all uncertain detections from current image"} position="bottom">
                    <button
                      onClick={handleDeleteUncertainDetections}
                      disabled={!hasUncertainDetectionsInCurrentImage}
                      className={cn(
                        "flex items-center justify-center w-10 h-10 rounded-lg border transition-all",
                        !hasUncertainDetectionsInCurrentImage ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed" : "bg-orange-100 border-orange-300 text-orange-700 hover:bg-orange-200 hover:border-orange-400"
                      )}
                    >
                      <Eraser className="h-4 w-4" />
                    </button>
                  </Tooltip>

                  <Tooltip content={!hasImages ? t("session_details.no_images_to_stage") : !isSessionReady ? "Session has uncertain detections or validation issues - resolve them before staging" : t("session_details.stage_changes")} position="bottom">
                    <button
                      onClick={handleStageChanges}
                      disabled={!isSessionReady || !hasImages}
                      className={cn(
                        "flex items-center justify-center w-10 h-10 rounded-lg border transition-all",
                        isSessionReady && hasImages ? "bg-green-100 border-green-300 text-green-700 hover:bg-green-200 hover:border-green-400" : "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                      )}
                    >
                      <CheckSquare className="h-4 w-4" />
                    </button>
                  </Tooltip>
                </>
              )}

              {/* Generate Report button - only show when session is completed */}
              {isSessionCompleted && (
                <Tooltip content="Generate a detailed report for this session" position="bottom">
                  <button
                    onClick={handleGenerateReport}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border transition-all bg-purple-100 border-purple-300 text-purple-700 hover:bg-purple-200 hover:border-purple-400"
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                </Tooltip>
              )}

              {isAdmin && (
                <Tooltip content={t("session_details.analysis_settings")} position="bottom">
                  <div className="relative" ref={settingsButtonRef}>
                    <button
                      onClick={() => (showSettings ? handleCloseSettings() : setShowSettings(true))}
                      className="flex items-center justify-center w-10 h-10 rounded-lg border transition-all bg-white border-purple-300 text-purple-600 hover:border-purple-400 hover:text-purple-700"
                    >
                      <Settings className="h-4 w-4" />
                      {saveStatus === "saving" && <div className="absolute -top-1 -right-1 w-3 h-3 border border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>}
                      {saveStatus === "saved" && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      {saveStatus === "error" && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                          <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>

                    <SessionSettingsDropdown
                      isOpen={showSettings}
                      onClose={handleCloseSettings}
                      settings={settings}
                      saveStatus={saveStatus}
                      hasUnsavedChanges={hasUnsavedChanges}
                      onUpdateSettings={(updates) => {
                        setSettings((prev) => ({ ...prev, ...updates }));
                        setHasUnsavedChanges(true);
                      }}
                      onSaveSettings={saveSessionSettings}
                      dialogRef={settingsDialogRef}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-4 flex-1 min-h-0 overflow-visible">
          <SectionGrid
            isSessionCompleted={isSessionCompleted}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragActive={isDragActive}
            sections={sections}
            sectionsWithStatus={sectionsWithStatus}
            selectedImageId={selectedImageId}
            handleSectionClick={handleSectionClick}
            handleEmptySectionClick={handleEmptySectionClick}
            loadAnalysisImage={loadAnalysisImage}
            handleViewValidation={handleViewValidation}
            handleViewSectionDetails={handleViewSectionDetails}
            unassignedImages={unassignedImages}
            setImageToAssign={setImageToAssign}
            setShowDirectCreateSectionModal={setShowDirectCreateSectionModal}
            onDeleteAnalysis={handleDeleteAnalysis}
            correctedUploadedImages={correctedUploadedImages}
            correctedAnalyses={correctedAnalyses}
            setSelectedImageId={setSelectedImageId}
            loadImage={loadAnalysisImage}
            getValidationCounts={getValidationCounts}
          />

          <div className="w-5/6 flex flex-col space-y-4 min-h-0">
            <ImageViewerPanel
              selectedImageId={selectedImageId}
              uploadedImages={correctedUploadedImages}
              analyses={correctedAnalyses}
              topK={settings.topK}
              isSessionCompleted={isSessionCompleted}
              isDrawingMode={isDrawingMode}
              isAdmin={isAdmin}
              classValidationStatus={classValidationMap}
              newlyCreatedDetectionId={newlyCreatedDetectionId}
              onClassOverride={handleClassOverride}
              onUserDrawnDetection={handleUserDrawnDetection}
              onDeleteDetection={handleDeleteDetection}
              onAutoOpenDone={() => setNewlyCreatedDetectionId(null)}
              onPrevImage={handlePrevImage}
              onNextImage={handleNextImage}
              hasPrevImage={hasPrevImage}
              hasNextImage={hasNextImage}
              loadAnalysisImage={loadAnalysisImage}
            />

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex-shrink-0">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="mt-2 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <AnalysisProgressModal isAnalyzing={isAnalyzingMultiple} progress={analysisProgress} processedImages={processedImages} />

      {showValidation && validationResult && (
        <ValidationResults
          validation={validationResult}
          onClose={() => {
            setShowValidation(false);
            setValidationResult(null);
          }}
        />
      )}

      <FailedDetectionNotification notification={failedDetectionNotification} isAdmin={isAdmin} />

      {/* Create Section Modal - opens when clicking on unassigned images */}
      {showDirectCreateSectionModal && session?.room_id && (
        <CreateSectionModal
          onClose={() => {
            setShowDirectCreateSectionModal(false);
            setImageToAssign(null);
          }}
          onSuccess={handleDirectCreateSectionSuccess}
          roomId={session.room_id}
          createSection={handleCreateSection}
          availableClasses={allClassNames}
          initialImage={imageToAssignBase64}
          initialExpectedItems={detectedClassesFromImage}
          title="Create Section from Image"
        />
      )}

      <ValidationResultsDialog
        isOpen={showValidationDialog}
        onClose={() => {
          setShowValidationDialog(false);
          setValidationDialogResult(null);
        }}
        validationResult={validationDialogResult}
      />

      {showSectionDetailsDialog && selectedSectionForDetails && (
        <SectionDetailsDialog
          section={selectedSectionForDetails}
          onClose={() => {
            setShowSectionDetailsDialog(false);
            setSelectedSectionForDetails(null);
          }}
        />
      )}
    </>
  );
};

export default SessionDetailsPage;
